import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { format, startOfWeek, endOfWeek, addWeeks, eachDayOfInterval, parseISO } from 'date-fns';
import { Clock, TrendingUp, Calendar, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal, Loader, Button } from '../common';
import { api } from '../../services/api';
import type { TimeEntry } from '../../types';
import { formatDurationFromMinutes } from '../../utils/attendance';
import {
  buildDayData,
  buildDayUserBreakdown,
  calculateSummaryStats,
  groupEntriesByDate,
  type DayData,
  type DayUserBreakdown,
  type BackendWorkSchedule,
} from '../../utils/attendanceSummary';
import { useDateFnsLocale } from '../../hooks/useDateFnsLocale';

interface AttendanceSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  selectedDate: string;
  backendWorkSchedule?: {
    workShifts: unknown;
    workScheduleDays: number[];
    timezone: string;
  } | null;
}

export const AttendanceSummaryModal: React.FC<AttendanceSummaryModalProps> = ({
  isOpen,
  onClose,
  isAdmin,
  selectedDate,
  backendWorkSchedule,
}) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();

  const [weekOffset, setWeekOffset] = useState(0);
  const [dayData, setDayData] = useState<DayData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [users, setUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);

  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const baseDate = useMemo(() => parseISO(selectedDate + 'T12:00:00'), [selectedDate]);

  const dateRange = useMemo(() => {
    const weekBase = addWeeks(baseDate, weekOffset);
    return {
      start: startOfWeek(weekBase, { weekStartsOn: 1 }),
      end: endOfWeek(weekBase, { weekStartsOn: 1 }),
    };
  }, [baseDate, weekOffset]);

  const days = useMemo(
    () => eachDayOfInterval({ start: dateRange.start, end: dateRange.end }),
    [dateRange],
  );

  const loadSummaryData = useCallback(async (signal: AbortSignal, silent = false) => {
    if (!isOpen) return;
    if (!silent) {
      setIsLoading(true);
    }
    setLoadError(null);

    try {
      const endpoint = isAdmin
        ? '/time-entries/admin/attendance-range'
        : '/time-entries/me/attendance-range';
      const startDate = format(dateRange.start, 'yyyy-MM-dd');
      const endDate = format(dateRange.end, 'yyyy-MM-dd');

      const requestParams: Record<string, unknown> = { startDate, endDate, limit: 5000 };
      if (isAdmin && selectedUserId) {
        requestParams.userId = selectedUserId;
      }

      const response = await api.get(endpoint, {
        params: requestParams,
        signal,
      });

      if (!response.data.success) {
        throw new Error('Failed to load attendance range');
      }

      const entries: TimeEntry[] = response.data.data.entries || [];
      const responseWorkSchedule = !isAdmin
        ? (response.data.data.userWorkSchedule as BackendWorkSchedule | null | undefined)
        : undefined;

      const activeWorkSchedule = responseWorkSchedule ?? backendWorkSchedule;
      const timezone = activeWorkSchedule?.timezone ?? browserTimezone;
      const grouped = groupEntriesByDate(entries, timezone);

      const results = days.map((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayEntries = grouped.get(dateStr) ?? [];
        return buildDayData(dateStr, dayEntries, isAdmin, activeWorkSchedule, new Date());
      });

      setDayData(results);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      console.error('Error loading summary data:', error);
      setLoadError(t('attendances.summary.errorLoading'));
      setDayData([]);
    } finally {
      if (!signal.aborted && !silent) {
        setIsLoading(false);
      }
    }
  }, [isOpen, days, isAdmin, backendWorkSchedule, dateRange, t, selectedUserId]);

  useEffect(() => {
    if (!isOpen) return;

    const controller = new AbortController();
    loadSummaryData(controller.signal);

    const intervalId = setInterval(() => {
      loadSummaryData(controller.signal, true);
    }, 60000);

    return () => {
      clearInterval(intervalId);
      controller.abort();
    };
  }, [isOpen, loadSummaryData, weekOffset, selectedDate, selectedUserId]);

  useEffect(() => {
    if (!isOpen || !isAdmin) return;

    let cancelled = false;
    setUsersLoading(true);
    api.get('/users/list/names')
      .then((response) => {
        if (cancelled) return;
        if (response.data.success) {
          const users = Array.isArray(response.data.data)
            ? (response.data.data as { id: string; firstName: string; lastName: string }[])
            : (response.data.data?.users ?? []);
          setUsers(users);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error loading users for summary selector:', error);
      })
      .finally(() => {
        if (!cancelled) {
          setUsersLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isAdmin]);

  const stats = useMemo(() => calculateSummaryStats(dayData), [dayData]);


  const weekRangeLabel = useMemo(
    () => `${format(dateRange.start, 'dd MMM', { locale: dateFnsLocale })} - ${format(dateRange.end, 'dd MMM yyyy', { locale: dateFnsLocale })}`,
    [dateRange, dateFnsLocale],
  );

  const isCurrentWeek = weekOffset === 0;

  const dayLabel = (dateStr: string) => {
    const d = parseISO(dateStr + 'T12:00:00');
    return format(d, 'EEE dd', { locale: dateFnsLocale });
  };

  const dayShortLabel = (dateStr: string) => {
    const d = parseISO(dateStr + 'T12:00:00');
    return format(d, 'EEEEE', { locale: dateFnsLocale });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('attendances.summary.title')}
      size="2xl"
    >
      <div className="space-y-6">
        {isAdmin && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label htmlFor="summary-user-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('attendances.summary.selectUser')}
            </label>
            <select
              id="summary-user-select"
              value={selectedUserId ?? ''}
              onChange={(e) => setSelectedUserId(e.target.value || null)}
              disabled={usersLoading}
              className="w-full sm:w-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">{t('attendances.summary.allOrganization')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Week navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => setWeekOffset((o) => o - 1)}
              className="px-3 py-2 text-sm font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('attendances.summary.previousWeek')}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setWeekOffset(0)}
              disabled={isCurrentWeek}
              className={`px-3 py-2 text-sm font-medium transition-colors border-l border-r border-gray-200 dark:border-gray-700 ${
                isCurrentWeek
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('attendances.summary.thisWeek')}
            </button>
            <button
              onClick={() => setWeekOffset((o) => o + 1)}
              className="px-3 py-2 text-sm font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('attendances.summary.nextWeek')}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {weekRangeLabel}
          </p>
        </div>

        {loadError && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 p-3 text-sm text-red-700 dark:text-red-300">
            {loadError}
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{t('attendances.summary.loading')}</p>
          </div>
        ) : (
          <>
            {/* Statistics cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatDurationFromMinutes(Math.round(stats.avgDailyMinutes))}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('attendances.summary.avgDailyWorkTime')}
                </p>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 p-2 rounded-lg">
                    <Clock className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatDurationFromMinutes(stats.totalNormalMinutes)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('attendances.summary.normalHours')}
                </p>
              </div>

              <div className={`rounded-xl p-4 ${stats.totalOvertimeMinutes > 0 ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className={`p-2 rounded-lg ${stats.totalOvertimeMinutes > 0 ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>
                    <BarChart3 className="w-5 h-5" />
                  </div>
                </div>
                <p className={`text-xl sm:text-2xl font-bold ${stats.totalOvertimeMinutes > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {formatDurationFromMinutes(stats.totalOvertimeMinutes)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('attendances.summary.overtimeHours')}
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 p-2 rounded-lg">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {formatDurationFromMinutes(stats.totalMinutes)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('attendances.summary.totalAccumulatedHours')}
                </p>
              </div>
            </div>

            {/* Visual stacked segmented bar: total week hours, each day proportional width */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                {t('attendances.summary.hoursPerDay')}
              </h3>

              <div className="flex h-14 sm:h-18 rounded-lg border border-gray-200 dark:border-gray-700">
                {dayData.map((day, idx) => {
                  const isLast = idx === dayData.length - 1;
                  const hasWork = day.totalMinutes > 0;
                  const normalMinutes = hasWork ? day.normalMinutes : 0;
                  const overtimeMinutes = hasWork ? day.overtimeMinutes : 0;
                  const isHovered = hoveredDay === day.date;

                  return (
                    <div
                      key={day.date}
                      className={`relative flex items-stretch ${!hasWork ? 'bg-gray-100 dark:bg-gray-800' : ''} ${!isLast ? 'border-r-2 border-gray-300 dark:border-gray-600' : ''} ${hasWork ? 'cursor-pointer' : ''} min-w-[2.5rem]`}
                      style={{ flex: hasWork ? day.totalMinutes : 0 }}
                      onMouseEnter={() => hasWork && setHoveredDay(day.date)}
                      onMouseLeave={() => setHoveredDay(null)}
                      onClick={() => {
                        if (!hasWork) return;
                        setHoveredDay((prev) => (prev === day.date ? null : day.date));
                      }}
                    >
                      {hasWork ? (
                        <>
                          {normalMinutes > 0 && (
                            <div
                              className="bg-blue-500 flex items-center justify-center relative overflow-hidden"
                              style={{ flex: normalMinutes }}
                            >
                              <span className="text-[10px] sm:text-xs font-medium text-white truncate px-1">
                                {formatDurationFromMinutes(normalMinutes)}
                              </span>
                            </div>
                          )}
                          {overtimeMinutes > 0 && (
                            <div
                              className="bg-orange-500 flex items-center justify-center relative overflow-hidden"
                              style={{ flex: overtimeMinutes }}
                            >
                              <span className="text-[10px] sm:text-xs font-medium text-white truncate px-1">
                                {formatDurationFromMinutes(overtimeMinutes)}
                              </span>
                            </div>
                          )}
                          {isHovered && (
                            <DayTooltip
                              day={day}
                              isAdmin={isAdmin}
                              dayLabel={dayLabel(day.date)}
                              t={t}
                            />
                          )}
                        </>
                      ) : null}
                      {!hasWork && (
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] sm:text-xs font-medium truncate px-1 text-gray-400 dark:text-gray-500">
                          {formatDurationFromMinutes(day.totalMinutes)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Day labels below bar */}
              <div className="flex mt-2">
                {dayData.map((day, idx) => {
                  const isLast = idx === dayData.length - 1;
                  const isToday = day.date === selectedDate;
                  return (
                    <div
                      key={day.date}
                      className={`text-center ${!isLast ? 'border-r-2 border-gray-200 dark:border-gray-700' : ''}`}
                      style={{ flex: day.totalMinutes > 0 ? day.totalMinutes : 0, minWidth: '2.5rem' }}
                    >
                      <span className={`text-[10px] sm:text-xs ${
                        isToday
                          ? 'font-bold text-primary-600 dark:text-primary-400'
                          : day.totalMinutes > 0
                            ? 'text-gray-600 dark:text-gray-400'
                            : 'text-gray-300 dark:text-gray-600'
                      }`}>
                        {dayShortLabel(day.date)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-blue-500" />
                  <span>{t('attendances.summary.normalHours')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-orange-500" />
                  <span>{t('attendances.summary.overtimeLegend')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" />
                  <span>{t('attendances.summary.noWorkDay')}</span>
                </div>
              </div>
            </div>

            {/* Detailed day list (mobile-friendly) */}
            <div className="sm:hidden">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('attendances.summary.dailyBreakdown')}
              </h3>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {dayData.filter((d) => d.totalMinutes > 0).map((day) => (
                  <div key={day.date} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2">
                    <span className="text-gray-600 dark:text-gray-400">{dayLabel(day.date)}</span>
                    <span className={`font-medium ${day.overtimeMinutes > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
                      {formatDurationFromMinutes(day.totalMinutes)}
                    </span>
                  </div>
                ))}
                {dayData.filter((d) => d.totalMinutes > 0).length === 0 && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                    {t('attendances.summary.noData')}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Close button */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose} className="w-full">
            {t('common.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface DayTooltipProps {
  day: DayData;
  isAdmin: boolean;
  dayLabel: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const DayTooltip: React.FC<DayTooltipProps> = ({ day, isAdmin, dayLabel, t }) => {
  const breakdown = useMemo<DayUserBreakdown[]>(
    () => buildDayUserBreakdown(day.date, day.entries, isAdmin, new Date()),
    [day.date, day.entries, isAdmin],
  );

  if (breakdown.length === 0) return null;

  const totalMinutes = breakdown.reduce((sum, b) => sum + b.totalMinutes, 0);

  return (
    <div
      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-64 max-w-[90vw] pointer-events-none"
      role="tooltip"
    >
      <div className="bg-gray-900 dark:bg-gray-800 text-white rounded-lg shadow-xl border border-gray-700 dark:border-gray-600 p-3 text-xs">
        <p className="font-semibold text-white mb-2 pb-1.5 border-b border-gray-700 dark:border-gray-600">
          {dayLabel}
        </p>
        <div className="space-y-1.5">
          {breakdown.map((user) => (
            <div key={user.userId} className="space-y-0.5">
              <p className="font-medium text-gray-100">
                {user.userName || t('attendances.summary.tooltipUnknownUser')}
              </p>
              <p className="text-gray-300 pl-2">
                {formatDurationFromMinutes(user.totalMinutes)}
                {' '}
                <span className="text-gray-400">
                  ({formatDurationFromMinutes(user.normalMinutes)} {t('attendances.summary.tooltipNormal')}{' '}
                  + {formatDurationFromMinutes(user.overtimeMinutes)} {t('attendances.summary.tooltipOvertime')})
                </span>
              </p>
            </div>
          ))}
        </div>
        <p className="font-semibold text-white mt-2 pt-1.5 border-t border-gray-700 dark:border-gray-600">
          {t('attendances.summary.tooltipTotal')}: {formatDurationFromMinutes(totalMinutes)}
        </p>
      </div>
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 dark:bg-gray-800 border-r border-b border-gray-700 dark:border-gray-600 transform rotate-45" />
    </div>
  );
};

export default AttendanceSummaryModal;
